import { formatCurrency } from './helpers';

// Calculate discount percentage
export const calculateDiscount = (price, comparePrice) => {
  if (!comparePrice || comparePrice <= price) return 0;
  const discount = ((comparePrice - price) / comparePrice) * 100;
  return Math.round(discount);
};

// Check if product has discount
export const hasDiscount = (product) => {
  return product.compare_price && product.compare_price > product.price;
};

// Get discount badge text
export const getDiscountText = (product) => {
  if (!hasDiscount(product)) return null;
  const discount = calculateDiscount(product.price, product.compare_price);
  return `${discount}% OFF`;
};

// Format price with currency
export const formatProductPrice = (price) => {
  return formatCurrency(price);
};

// Get stock status
export const getStockStatus = (product) => {
  if (!product.track_inventory) {
    return { status: 'available', label: 'In Stock', color: 'success' };
  }

  if (product.quantity === 0) {
    return { status: 'out_of_stock', label: 'Out of Stock', color: 'error' };
  }

  if (product.quantity <= 5) {
    return { status: 'low_stock', label: `Only ${product.quantity} left`, color: 'warning' };
  }

  return { status: 'in_stock', label: 'In Stock', color: 'success' };
};

// Check if product is in stock
export const isInStock = (product) => {
  if (!product.track_inventory) return true;
  return product.quantity > 0;
};

// Get image URL (handles relative URLs for hosted environments)
export const getImageUrl = (url) => {
  if (!url) return '/placeholder-product.png';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  
  const apiBase = window._env_?.VITE_API_URL || import.meta.env.VITE_API_URL || "http://localhost:5000/api/v1";
  const baseUrl = apiBase.replace(/\/api\/v[0-9]+$/, '').replace(/\/$/, '');
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Get primary image
export const getPrimaryImage = (product) => {
  if (product.images && product.images.length > 0) {
    return getImageUrl(product.images[0]);
  }
  return '/placeholder-product.png'; // Default placeholder
};

// Get all images
export const getAllImages = (product) => {
  if (!product.images) return [];
  return product.images.map(getImageUrl);
};

// Generate slug from name
export const generateSlug = (name) => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Calculate profit margin
export const calculateProfitMargin = (price, cost) => {
  if (!cost || cost === 0) return 0;
  return ((price - cost) / price) * 100;
};

// Format weight
export const formatWeight = (weight) => {
  if (!weight) return 'N/A';
  return `${weight} kg`;
};

// Group products by category
export const groupByCategory = (products) => {
  return products.reduce((acc, product) => {
    const categoryId = product.category_id;
    if (!acc[categoryId]) {
      acc[categoryId] = [];
    }
    acc[categoryId].push(product);
    return acc;
  }, {});
};

// Sort products
export const sortProducts = (products, sortBy, order = 'asc') => {
  const sorted = [...products];
  
  sorted.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'price':
        aValue = parseFloat(a.price);
        bValue = parseFloat(b.price);
        break;
      case 'created_at':
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
        break;
      case 'quantity':
        aValue = a.quantity;
        bValue = b.quantity;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });
  
  return sorted;
};

// Filter products
export const filterProducts = (products, filters) => {
  return products.filter(product => {
    // Category filter
    if (filters.category_id && product.category_id !== filters.category_id) {
      return false;
    }
    
    // Price range filter
    if (filters.min_price && parseFloat(product.price) < parseFloat(filters.min_price)) {
      return false;
    }
    if (filters.max_price && parseFloat(product.price) > parseFloat(filters.max_price)) {
      return false;
    }
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const nameMatch = product.name.toLowerCase().includes(searchLower);
      const descMatch = product.description?.toLowerCase().includes(searchLower);
      if (!nameMatch && !descMatch) {
        return false;
      }
    }
    
    // Featured filter
    if (filters.is_featured !== undefined && filters.is_featured !== '') {
      if (product.is_featured !== filters.is_featured) {
        return false;
      }
    }
    
    // Stock filter
    if (filters.in_stock && !isInStock(product)) {
      return false;
    }
    
    return true;
  });
};

// Get price range from products
export const getPriceRange = (products) => {
  if (!products || products.length === 0) {
    return { min: 0, max: 0 };
  }
  
  const prices = products.map(p => parseFloat(p.price));
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
  };
};

// Validate product data
export const isValidProduct = (product) => {
  return !!(
    product.name &&
    product.category_id &&
    product.price &&
    parseFloat(product.price) >= 0
  );
};

export default {
  calculateDiscount,
  hasDiscount,
  getDiscountText,
  formatProductPrice,
  getStockStatus,
  isInStock,
  getPrimaryImage,
  getAllImages,
  generateSlug,
  calculateProfitMargin,
  formatWeight,
  groupByCategory,
  sortProducts,
  filterProducts,
  getPriceRange,
  isValidProduct,
};